import React, { useState, useEffect } from 'react';
import { Clock, MapPin, Play, Square, CheckCircle, Camera, AlertCircle } from 'lucide-react';
import { 
  centres as centresData, 
  pointages as pointagesData,
  getPointageEnCours,
  getCentreById 
} from '../../data/mockData';

function Pointage({ user }) {
  const [centres, setCentres] = useState(centresData.filter(c => c.actif));
  const [centreSelectionne, setCentreSelectionne] = useState('');
  const [pointageActif, setPointageActif] = useState(null);
  const [heureDebut, setHeureDebut] = useState('');
  const [coordonnees, setCoordonnees] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [photoAtelier, setPhotoAtelier] = useState(null);
  const [gpsRefuse, setGpsRefuse] = useState(false);

  useEffect(() => {
    // Vérifier s'il y a un pointage en cours
    const pointageEnCours = getPointageEnCours(user.id);
    if (pointageEnCours) {
      setPointageActif(pointageEnCours);
      setHeureDebut(pointageEnCours.heureDebut);
    }
    
    // Obtenir les coordonnées GPS
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCoordonnees({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setGpsRefuse(false);
        },
        (error) => {
          console.error('Erreur GPS:', error);
          setGpsRefuse(true);
          setMessage({ 
            type: 'warning', 
            text: 'Impossible d\'obtenir votre position GPS. Vous pouvez continuer sans.' 
          });
        }
      );
    }
  }, [user.id]);

  const handlePhotoAtelier = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoAtelier(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDebut = (e) => {
    e.preventDefault();
    
    if (!centreSelectionne) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un centre' });
      return;
    }

    const now = new Date();
    const heureActuelle = now.toTimeString().slice(0, 5);
    
    const nouveauPointage = {
      id: pointagesData.length + 1,
      technicienId: user.id,
      centreId: parseInt(centreSelectionne),
      date: now.toISOString().split('T')[0],
      heureDebut: heureActuelle,
      heureFin: null,
      coordonneesGPSDebut: coordonnees,
      coordonneesGPSFin: null,
      gpsRefuse: gpsRefuse,
      enCours: true,
    };

    setPointageActif(nouveauPointage);
    setHeureDebut(heureActuelle);
    setMessage({ type: 'success', text: 'Pointage démarré avec succès!' });
    
    console.log('Nouveau pointage:', nouveauPointage);
  };

  const handleFin = () => {
    if (!pointageActif) return;

    const now = new Date();
    const heureActuelle = now.toTimeString().slice(0, 5);
    
    const pointageFini = {
      ...pointageActif,
      heureFin: heureActuelle,
      coordonneesGPSFin: coordonnees,
      photoFin: photoAtelier,
      enCours: false,
    };

    console.log('Pointage terminé:', pointageFini);
    
    setPointageActif(null);
    setHeureDebut('');
    setCentreSelectionne('');
    setPhotoAtelier(null);
    setMessage({ type: 'success', text: 'Fin de journée enregistrée!' });
  };

  const calculerDuree = () => {
    if (!heureDebut) return '00h00';
    
    const [hd, md] = heureDebut.split(':').map(Number);
    const now = new Date();
    const heureActuelle = now.getHours();
    const minuteActuelle = now.getMinutes();
    
    const totalMinutes = (heureActuelle * 60 + minuteActuelle) - (hd * 60 + md);
    const heures = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${heures.toString().padStart(2, '0')}h${minutes.toString().padStart(2, '0')}`;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Pointage</h1>
          <p className="text-muted">Gestion de votre temps de travail</p>
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="pointage-container">
        {!pointageActif ? (
          <div className="pointage-card">
            <div className="pointage-card-header">
              <Play size={32} className="icon-success" />
              <h2>Démarrer votre journée</h2>
              <p className="text-muted">Sélectionnez le centre où vous travaillez aujourd'hui</p>
            </div>

            <form onSubmit={handleDebut} className="pointage-form">
              <div className="form-group">
                <label htmlFor="centre">Centre de travail *</label>
                <select
                  id="centre"
                  value={centreSelectionne}
                  onChange={(e) => setCentreSelectionne(e.target.value)}
                  required
                >
                  <option value="">Sélectionnez un centre</option>
                  {centres.map(centre => (
                    <option key={centre.id} value={centre.id}>
                      {centre.nom} - {centre.ville}
                    </option>
                  ))}
                </select>
              </div>

              {coordonnees && (
                <div className="info-box info-success">
                  <MapPin size={20} />
                  <div>
                    <p><strong>Position GPS capturée</strong></p>
                    <p className="text-sm">
                      {coordonnees.latitude.toFixed(6)}, {coordonnees.longitude.toFixed(6)}
                    </p>
                  </div>
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-lg btn-block">
                <Play size={24} />
                Démarrer ma journée
              </button>
            </form>
          </div>
        ) : (
          <div className="pointage-card pointage-active">
            <div className="pointage-card-header">
              <CheckCircle size={32} className="icon-success" />
              <h2>Journée en cours</h2>
              <p className="text-muted">
                {getCentreById(pointageActif.centreId)?.nom}
              </p>
            </div>

            <div className="pointage-info">
              <div className="pointage-stat">
                <Clock size={24} className="icon-primary" />
                <div>
                  <p className="text-muted">Heure de début</p>
                  <p className="stat-value">{heureDebut}</p>
                </div>
              </div>

              <div className="pointage-stat">
                <Clock size={24} className="icon-primary" />
                <div>
                  <p className="text-muted">Durée écoulée</p>
                  <p className="stat-value">{calculerDuree()}</p>
                </div>
              </div>

              {pointageActif.coordonneesGPSDebut && (
                <div className="pointage-stat">
                  <MapPin size={24} className="icon-primary" />
                  <div>
                    <p className="text-muted">Position de début</p>
                    <p className="text-sm">
                      {pointageActif.coordonneesGPSDebut.latitude.toFixed(6)},<br />
                      {pointageActif.coordonneesGPSDebut.longitude.toFixed(6)}
                    </p>
                  </div>
                </div>
              )}

              {pointageActif.gpsRefuse && (
                <div className="info-box info-warning">
                  <AlertCircle size={20} />
                  <div>
                    <p><strong>GPS non disponible</strong></p>
                    <p className="text-sm">La géolocalisation a été refusée ou n'est pas disponible.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="fin-journee-section">
              <h3>Terminer votre journée</h3>
              <p className="text-muted text-sm">
                Prenez une photo de votre aire de lavage pour montrer qu'elle est impeccable
              </p>

              <div className="photo-section">
                <label className="photo-label">
                  <Camera size={24} />
                  <span>Photo de l'aire de lavage</span>
                  <input 
                    type="file" 
                    accept="image/*" 
                    capture="environment"
                    onChange={handlePhotoAtelier}
                    className="photo-input"
                  />
                </label>
                
                {photoAtelier && (
                  <div className="photo-preview">
                    <img src={photoAtelier} alt="Photo atelier" />
                    <button 
                      onClick={() => setPhotoAtelier(null)} 
                      className="btn btn-sm btn-secondary"
                      style={{ marginTop: '0.5rem' }}
                    >
                      Supprimer la photo
                    </button>
                  </div>
                )}
              </div>
            </div>

            <button onClick={handleFin} className="btn btn-danger btn-lg btn-block">
              <Square size={24} />
              Terminer ma journée
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Pointage;

